<template>
  <Teleport to="body">
    <vue-final-modal
      v-model="showModal"
      classes="modal-container"
      content-class="modal-content plan-modal"
      overlay-class="vfm__overlay"
      :click-to-close="true"
      :lock-scroll="true"
      :hide-overlay="false"
      :esc-to-close="true"
      @click:overlay="closeModal"
      @click="handleOutsideClick"
    >
      <div class="p-3" @click.stop>
        <div class="card">
          <div class="card-body">
            <div class="row py-2">
              <div class="col-md-4">
                日時:
              </div>
              <div class="col-md-8">
                {{ dayString }}
              </div>
            </div>
            <div class="row py-2">
              <div class="col-md-4">
                利用者:
              </div>
              <div class="col-md-8">
                {{ plan?.user?.display_name || '未設定' }}
              </div>
            </div>
            <div class="row py-2">
              <div class="col-md-4">
                担当者:
              </div>
              <div class="col-md-8">
                <div v-for="staff in plan?.staff" :key="staff.id">
                  {{ staff.display_name }}
                </div>
              </div>
            </div>
            <div class="row py-2">
              <div class="col-md-4">
                サービスコード:
              </div>
              <div class="col-md-8">
                <template v-if="plan?.service?.service_type">
                  {{ plan.service.service_type.display_name }}
                </template>
                <template v-if="plan?.service?.service_code">
                  &nbsp;&nbsp;-&nbsp;&nbsp;
                  {{ plan.service.service_code.display_name }}
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12 text-right">
            <a href="#" @click.prevent="closeModal" class="btn btn-link">キャンセル</a>
            <span class="mx-1"></span>
            <button type="button" class="action-button btn btn-secondary btn-rounded" @click="showCopyModal"
                    :disabled="isProcessing">
              <i class="fa fa-copy"></i>&nbsp;コピーする
            </button>
            <span class="mx-1"></span>
            <button type="button" class="action-button btn btn-success btn-rounded" @click="edit"
                    :disabled="isProcessing" v-if="!isApproved">
              <i class="fa fa-edit"></i>&nbsp;編集する
            </button>
          </div>
        </div>
      </div>
    </vue-final-modal>

    <vue-final-modal
      v-model="showCopyModalValue"
      classes="modal-container"
      content-class="modal-content copy-modal"
      overlay-class="vfm__overlay"
      :click-to-close="true"
      :lock-scroll="true"
      :hide-overlay="false"
      :esc-to-close="true"
    >
      <div class="content p-3" @click.stop>
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-md-9">
                <div class="mb-3">
                  <label class="form-label">コピー先</label>
                  <multi-select-calendar v-if="isSakura" v-model="date" @change="onDayClick"/>
                  <input type="date" class="form-control" v-else v-model="copyDest">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row px-3">
          <div class="col-md-12 text-end">
            <a href="#" @click.prevent="() => showCopyModalValue = false" class="btn btn-link">キャンセル</a>
            <span class="mx-1"></span>
            <button type="button" class="btn btn-success"
                    @click="copy"
                    :disabled="isProcessing || (isSakura ? selectedDate.length === 0 : !copyDest)">
              <i class="fa fa-copy"></i>&nbsp;コピーする
            </button>
          </div>
        </div>
      </div>
    </vue-final-modal>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import { useToast } from 'bootstrap-vue-next'
import dayjs from 'dayjs'
import { useStore } from 'vuex'
import api from '../libs/Api'
import Constants from '../constants'
import utils from '../libs/Utils'
import MultiSelectCalendar from '../components/MultiSelectCalendar'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  plan: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'edit', 'change', 'cancel'])

const store = useStore()
const profile = computed(() => store.state.session.profile)
const toast = useToast()

// State
const showCopyModalValue = ref(false)
const isSakura = ref(false)
const copyDest = ref(null)
const isProcessing = ref(false)
const date = ref([])
const selectedDate = ref([])

// Computed
const showModal = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
  }
})

const dayString = computed(() => {
  if (!props.plan?.service) return ''

  return utils.formatDay(props.plan.service.start_day) + ' ' +
    dayjs(props.plan.service.start_day, 'YYYY-MM-DD').format('(ddd)') + ' ' +
    utils.formatHHMM(props.plan.service.start_hhmm) + ' 〜 ' +
    utils.formatHHMM(props.plan.service.end_hhmm)
})

const isApproved = computed(() => {
  return props.plan?.plan_status_cd === Constants.Code.PlanStatus.APPROVED
})

// Methods
const showToast = (message, variant = 'success') => {
  toast.show({
    title: variant === 'success' ? '成功' : 'エラー',
    body: message,
    variant,
    delay: 2000
  })
}

const closeModal = () => {
  emit('cancel')
  emit('update:modelValue', false)
}

const edit = async () => {
  const planToEdit = props.plan ? JSON.parse(JSON.stringify(props.plan)) : {}

  const editData = {
    plan: {
      id: planToEdit.id,
      service: {
        start_day: planToEdit.service?.start_day,
        start_hhmm: planToEdit.service?.start_hhmm,
        end_day: planToEdit.service?.end_day,
        end_hhmm: planToEdit.service?.end_hhmm,
        service_type_id: planToEdit.service?.service_type_id,
        service_code_id: planToEdit.service?.service_code_id,
        service_unique_code_id: planToEdit.service?.service_unique_code_id,
        service_teresa_unique_code_id: planToEdit.service?.service_teresa_unique_code_id,
        service_type_group_id: planToEdit.service?.service_type_group_id,
        destination: planToEdit.service?.destination || '',
        specific_office_addition_cd: planToEdit.service?.specific_office_addition_cd || '000',
        special_addition_cd: planToEdit.service?.special_addition_cd || '000',
        own_expense_flag: Boolean(planToEdit.service?.own_expense_flag),
        wheelchair_taxi_flag: Boolean(planToEdit.service?.wheelchair_taxi_flag),
        service_items: Array.isArray(planToEdit.service?.service_items)
          ? planToEdit.service.service_items.map(item => ({
            id: item.id,
            care_item_id: item.care_item_id,
            service_id: item.service_id
          })) : []
      },
      staff: Array.isArray(planToEdit.staff) ? planToEdit.staff.map(s => ({
        id: s.id,
        staff_id: s.id,
        display_name: s.display_name
      })) : [],
      instruction: planToEdit.instruction || '',
      user: planToEdit.user ? {
        id: planToEdit.user.id,
        display_name: planToEdit.user.display_name
      } : null
    },
    params: {
      id: planToEdit.id,
      start_day: planToEdit.service?.start_day,
      start_hhmm: planToEdit.service?.start_hhmm,
      end_day: planToEdit.service?.end_day,
      end_hhmm: planToEdit.service?.end_hhmm,
      user_id: planToEdit.user?.id,
      staff_id: planToEdit.staff?.[0]?.id
    }
  }

  await store.dispatch('careplan/setPlanToEdit', editData)

  emit('edit', editData)

  showModal.value = false
}

const handleOutsideClick = (event) => {
  if (event.target.classList.contains('vfm__overlay')) {
    closeModal()
  }
}

const showCopyModal = () => {
  copyDest.value = utils.formatDay(props.plan.service.start_day)
  date.value = []
  selectedDate.value = []
  showCopyModalValue.value = true
}

const copy = async () => {
  if (isProcessing.value || (!isSakura.value && !copyDest.value)) return
  if (isSakura.value && selectedDate.value.length === 0) return

  try {
    isProcessing.value = true

    if (isSakura.value) {
      const dests = selectedDate.value.map(date =>
        dayjs(date, 'YYYY-MM-DD').format('YYYYMMDD')
      )

      await api.copyOnePlan({
        id: props.plan.id,
        dst: dests
      })

      selectedDate.value = []
    } else {
      await api.copyOnePlan({
        id: props.plan.id,
        dst: [dayjs(copyDest.value, 'YYYY-MM-DD').format('YYYYMMDD')]
      })
    }

    showToast('コピーしました。')
    showCopyModalValue.value = false
    closeModal()
    emit('change', props.plan)
  } catch (error) {
    console.error(error)
    showToast('エラーが発生しました。', 'danger')
  } finally {
    isProcessing.value = false
  }
}

const onDayClick = (selectedDates) => {
  selectedDate.value = selectedDates
}

// Lifecycle hooks
onMounted(() => {
  isSakura.value = (Constants.FunctionMask.SAKURA & profile.value.site.function_mask) === Constants.FunctionMask.SAKURA
})
</script>

<style>
.modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: #fff;
  z-index: 2001;
}

.plan-modal {
  width: 650px !important;
  min-height: 330px !important;
}

.copy-modal {
  width: 450px !important;
  min-height: auto !important;
}

.btn-link {
  text-decoration: none;
  color: #6c757d;
}

.btn-link:hover {
  color: #4b5156;
  text-decoration: underline;
}

.btn {
  cursor: pointer;
  padding: 3px !important;
  margin: 0 8px !important;
}
</style>
